import React, { SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
  FormikHelpers as FormikActions,
} from "formik"
import { Grid, Box, FormLabel, Typography, styled } from "@mui/material"
import { colors } from "theme"

import i18n from "config/i18n"
import InputSelect from "components/ui/InputSelect"
import InputSlider from "components/ui/InputSlider"
import Button from "components/ui/Submit"
import Toggle from "components/ui/InputToggle"
import { formatMoney } from "helpers/formatters"
import { calculateAmount, calculatePeriod } from "helpers/payment_schedule"
import { PLAN_STANDARD, PLAN_SILVER, PLAN_GOLD } from "config"
import {
  PaymentScheduleDate,
  PaymentScheduleFormValues,
  PaymentScheduleSliderOptions,
  PlanNameType,
} from "types"

interface FormProps {
  initialValues: PaymentScheduleFormValues
  options: PaymentScheduleSliderOptions
  dates: PaymentScheduleDate[]
  isChoose: boolean
  chosenPlan: PlanNameType
  onChoose: (plan: PlanNameType) => void
  onChange: (period: number) => void
  onSubmit: (
    values: PaymentScheduleFormValues,
    actions: FormikActions<object>,
  ) => void
}

type Props = FormikProps<PaymentScheduleFormValues> & FormProps

const PaymentScheduleForm = ({
  options,
  dates,
  chosenPlan,
  isChoose,
  values,
  setFieldValue,
  isSubmitting,
  onChoose,
  onChange,
}: Props) => {
  const { t } = useTranslation()
  const { amountOptions, periodOptions } = options
  const balance = amountOptions.max

  const PLANS = [
    {
      id: 1,
      label: t("features.plan.type.standard.title"),
      value: PLAN_STANDARD,
    },
    {
      id: 2,
      label: t("features.plan.type.silver.title"),
      value: PLAN_SILVER,
    },
    {
      id: 3,
      label: t("features.plan.type.gold.title"),
      value: PLAN_GOLD,
    },
  ]

  const handleAmountChange = (
    event: SyntheticEvent | Event,
    value: number | Array<number>,
  ) => {
    const amount = Array.isArray(value) ? value[0] : value
    const period = calculatePeriod(balance, amount)

    setFieldValue("period", period)
    onChange(period)
  }

  const handlePeriodChange = (
    event: SyntheticEvent | Event,
    value: number | Array<number>,
  ) => {
    const period = Array.isArray(value) ? value[0] : value
    const amount = calculateAmount(balance, period)

    setFieldValue("amount", amount)
    onChange(period)
  }

  const handleClick = (event: React.MouseEvent, newValue: PlanNameType) => {
    if (newValue !== null) {
      onChoose(newValue)
    } else {
      onChoose(chosenPlan)
    }
  }

  return (
    <Content>
      <Form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Label>{t("features.payment_schedule.amount")}</Label>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                <Value>{formatMoney(values.amount)}</Value>
              </Grid>
            </Grid>
            <Field name="amount">
              {({ field }: FieldProps) => (
                <InputSlider
                  {...field}
                  disabled={isSubmitting}
                  min={amountOptions.min}
                  max={amountOptions.max}
                  step={amountOptions.step}
                  onChangeCommitted={handleAmountChange}
                />
              )}
            </Field>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Help>{formatMoney(amountOptions.min)}</Help>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                <Help>{formatMoney(amountOptions.max)}</Help>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Label>{t("features.payment_schedule.period")}</Label>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                <Value>
                  {t("features.payment_schedule.form.period", {
                    number: values.period,
                  })}
                </Value>
              </Grid>
            </Grid>
            <Field name="period">
              {({ field }: FieldProps) => (
                <InputSlider
                  {...field}
                  disabled={isSubmitting}
                  min={periodOptions.min}
                  max={periodOptions.max}
                  step={periodOptions.step}
                  onChangeCommitted={handlePeriodChange}
                />
              )}
            </Field>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Help>
                  {t("features.payment_schedule.form.period", {
                    number: periodOptions.min,
                  })}
                </Help>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                <Help>
                  {t("features.payment_schedule.form.period", {
                    number: periodOptions.max,
                  })}
                </Help>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {isChoose ? (
              <Toggle items={PLANS} value={chosenPlan} onChange={handleClick} />
            ) : (
              <>
                <Label>{t("features.payment_schedule.start_date")}</Label>
                <Field name="start_date">
                  {({ field }: FieldProps) => (
                    <InputSelect fullWidth disabled={isSubmitting} {...field}>
                      {dates.map((c) => (
                        <option key={c.id} value={c.value}>
                          {c.value}
                        </option>
                      ))}
                    </InputSelect>
                  )}
                </Field>
              </>
            )}
          </Grid>
        </Grid>

        <Submit type="submit" disabled={isSubmitting}>
          {isChoose
            ? t("features.payment_schedule.form.continue")
            : t("features.payment_schedule.form.submit")}
        </Submit>
      </Form>
    </Content>
  )
}

const PaymentScheduleFormWithFormik = withFormik<
  FormProps,
  PaymentScheduleFormValues
>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  handleSubmit: (values, actions) => actions.props.onSubmit(values, actions),
  validate: (values: PaymentScheduleFormValues) => {
    const errors: FormikErrors<PaymentScheduleFormValues> = {}

    if (!values.amount) {
      errors.amount = i18n.t("validation.field.required", {
        field: i18n.t("features.payment_schedule.amount"),
      })
    }

    if (!values.period) {
      errors.period = i18n.t("validation.field.required", {
        field: i18n.t("features.payment_schedule.period"),
      })
    }

    if (!values.start_date) {
      errors.start_date = i18n.t("validation.field.required", {
        field: i18n.t("features.payment_schedule.start_date"),
      })
    }

    return errors
  },
})(PaymentScheduleForm)

export default PaymentScheduleFormWithFormik

const Content = styled(Box)(() => ({
  margin: "0 auto 23px",
  maxWidth: "464px",
  position: "relative",
}))

const Label = styled(FormLabel)(() => ({
  color: colors.indigo,
  fontSize: "0.875rem",
  fontWeight: "600",
  lineHeight: "17px",
}))

const Value = styled(Typography)(() => ({
  color: colors.persianGreen,
  fontSize: "0.875rem",
  fontWeight: "600",
  lineHeight: "17px",
}))

const Help = styled(Typography)(() => ({
  color: colors.chineseSilver,
  fontSize: "0.75rem",
  fontWeight: "600",
  lineHeight: "16px",
}))

const Submit = styled(Button)(() => ({
  borderRadius: "21px",
  display: "block",
  margin: "34px auto 24px auto",
  width: "206px",
}))
